import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { User, Building2, Sparkles, Search } from 'lucide-react'
import { VARIABLES, VARIABLE_CATEGORIES, Variable, VariableCategory } from '@/lib/variables'
import HighlightMatch from '@/components/HighlightMatch'

const CATEGORY_ICONS: Record<VariableCategory, React.ElementType> = {
  person: User,
  company: Building2,
  author: Sparkles,
}

const CATEGORY_ORDER: VariableCategory[] = ['person', 'author', 'company']

interface VariablePickerProps {
  onSelect: (variable: Variable) => void
  onClose: () => void
}

export default function VariablePicker({ onSelect, onClose }: VariablePickerProps) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const filteredVariables = useMemo(() => {
    if (!query) return VARIABLES
    const lowerQuery = query.toLowerCase()
    return VARIABLES.filter(
      (v) => v.label.toLowerCase().includes(lowerQuery) || v.key.toLowerCase().includes(lowerQuery)
    )
  }, [query])

  const groupedVariables = useMemo(() => {
    const groups: Record<VariableCategory, Variable[]> = {
      person: [],
      company: [],
      author: [],
    }
    filteredVariables.forEach((v) => {
      groups[v.category].push(v)
    })
    return groups
  }, [filteredVariables])

  const flattenedVariables = useMemo(() => {
    const result: Variable[] = []
    CATEGORY_ORDER.forEach((category) => {
      result.push(...groupedVariables[category])
    })
    return result
  }, [groupedVariables])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((i) => Math.min(i + 1, flattenedVariables.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((i) => Math.max(i - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          if (flattenedVariables[selectedIndex]) {
            onSelect(flattenedVariables[selectedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    },
    [flattenedVariables, selectedIndex, onSelect, onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.querySelector('[data-selected="true"]')
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [selectedIndex])

  let currentIndex = 0

  return (
    <div className="max-h-96 overflow-hidden">
      {/* Search */}
      <div className="p-3 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Pesquisar atributos"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* Variable list */}
      <div ref={listRef} className="overflow-y-auto max-h-72">
        {CATEGORY_ORDER.map((category) => {
          const vars = groupedVariables[category]
          if (vars.length === 0) return null

          const Icon = CATEGORY_ICONS[category]
          const categoryConfig = VARIABLE_CATEGORIES[category]

          return (
            <div key={category}>
              <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 flex items-center gap-2 sticky top-0">
                <Icon className="w-3.5 h-3.5" />
                {categoryConfig.label}
              </div>
              {vars.map((variable) => {
                const index = currentIndex++
                const isSelected = index === selectedIndex

                return (
                  <button
                    key={variable.key}
                    type="button"
                    data-selected={isSelected}
                    onClick={() => onSelect(variable)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between cursor-pointer ${
                      isSelected ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50'
                    }`}
                  >
                    <HighlightMatch text={variable.label} query={query} />
                    <span className="text-xs text-gray-400 font-mono">{variable.key}</span>
                  </button>
                )
              })}
            </div>
          )
        })}

        {flattenedVariables.length === 0 && (
          <div className="p-4 text-sm text-gray-500 text-center">Nenhuma variavel encontrada</div>
        )}
      </div>
    </div>
  )
}
